import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  defaultMapDiscoverFilters,
  mapDiscoverFiltersActive,
  MAP_DATE_PRESET_IDS,
} from '../../../utils/discoverEventFilters';
import './MapFiltersPanel.css';

function mergeFiltersToDraft(f) {
  const merged = { ...defaultMapDiscoverFilters(), ...f };
  if ((merged.dateFrom || merged.dateTo) && !merged.datePreset) {
    merged.datePreset = 'custom';
  }
  return merged;
}

function MapFiltersIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M4 6h16M8 12h8M10 18h4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="6" cy="6" r="1.75" fill="currentColor" />
      <circle cx="16" cy="12" r="1.75" fill="currentColor" />
      <circle cx="14" cy="18" r="1.75" fill="currentColor" />
    </svg>
  );
}

function MapFiltersCloseIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M8.5 8.5 15.5 15.5M15.5 8.5 8.5 15.5"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function countActiveMapFilters(f) {
  if (!f) return 0;

  let count = 0;
  const hasQuickDatePreset = f.datePreset && MAP_DATE_PRESET_IDS.includes(f.datePreset);
  const hasManualDateRange = (f.dateFrom || f.dateTo) && (!f.datePreset || f.datePreset === 'custom');

  if (hasQuickDatePreset || hasManualDateRange) count += 1;
  if (f.priceMin !== '' || f.priceMax !== '') count += 1;
  if (f.isFree === true || f.isFree === false) count += 1;
  if (f.minParticipants) count += 1;
  if (f.maxParticipants) count += 1;
  if (f.friendsOnly) count += 1;
  if (f.mineOnly) count += 1;

  return count;
}

/**
 * Przycisk + panel filtrów wydarzeń na mapie (pod wyszukiwaniem).
 */
function MapFiltersPanel({
  filtersOpen,
  onToggleOpen,
  filters,
  activeFilterCount = null,
  onApply,
  onReset,
  friendsFilterDisabled = false,
  mineFilterDisabled = false,
}) {
  const { t } = useTranslation(['discover', 'shared']);
  const [draft, setDraft] = useState(() => mergeFiltersToDraft(filters));

  useEffect(() => {
    setDraft(mergeFiltersToDraft(filters));
  }, [filters]);

  const computedActiveFilterCount = countActiveMapFilters(filters);
  const visibleActiveFilterCount =
    typeof activeFilterCount === 'number' && activeFilterCount >= 0
      ? activeFilterCount
      : computedActiveFilterCount;
  const hasActive = mapDiscoverFiltersActive(filters) || visibleActiveFilterCount > 0;

  const setField = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const toggleDatePreset = (preset) => {
    setDraft((d) => {
      const turningOff = d.datePreset === preset;
      return {
        ...d,
        datePreset: turningOff ? '' : preset,
        dateFrom: turningOff ? d.dateFrom : '',
        dateTo: turningOff ? d.dateTo : '',
      };
    });
  };

  const toggleCustomDateRange = () => {
    setDraft((d) => {
      if (d.datePreset === 'custom') {
        return { ...d, datePreset: '', dateFrom: '', dateTo: '' };
      }
      return {
        ...d,
        datePreset: 'custom',
        dateFrom: '',
        dateTo: '',
      };
    });
  };

  const apply = () => {
    onApply(draft);
    onToggleOpen(false);
  };

  const reset = () => {
    const cleared = defaultMapDiscoverFilters();
    setDraft(cleared);
    onReset(cleared);
    onToggleOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className={`map-filters-floating-button ${filtersOpen ? 'active' : ''} ${hasActive ? 'has-filters' : ''}`}
        onClick={() => onToggleOpen(!filtersOpen)}
        title={t('discover.map.filtersTitle', 'Filtry wydarzeń')}
        aria-expanded={filtersOpen}
        aria-controls="map-filters-panel"
      >
        <MapFiltersIcon className="map-filters-floating-icon" />
        {visibleActiveFilterCount > 0 && (
          <span className="map-filters-floating-badge" aria-hidden="true">
            {visibleActiveFilterCount > 9 ? '9+' : visibleActiveFilterCount}
          </span>
        )}
      </button>

      <div
        className={`map-filters-overlay ${filtersOpen ? 'open' : ''}`}
        id="map-filters-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="map-filters-panel-title"
        aria-hidden={!filtersOpen}
      >
        <div className="map-filters-container">
          <div className="map-filters-header">
            <h3 className="map-filters-title" id="map-filters-panel-title">
              {t('discover.map.filterEvents', 'Filtruj wydarzenia')}
            </h3>
            <button
              type="button"
              className="map-filters-close-button"
              onClick={() => onToggleOpen(false)}
              aria-label={t('shared:common.close', 'Zamknij')}
            >
              <MapFiltersCloseIcon className="map-filters-close-icon" />
            </button>
          </div>

          <div className="map-filters-fields">
            <span className="map-filters-label" id="map-filter-date-presets-label">
              {t('discover.map.datePreset.groupLabel', 'Szybki zakres dat')}
            </span>
            <div
              className="map-filters-presets"
              role="group"
              aria-labelledby="map-filter-date-presets-label"
            >
              {MAP_DATE_PRESET_IDS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`map-filters-preset ${draft.datePreset === preset ? 'active' : ''}`}
                  onClick={() => toggleDatePreset(preset)}
                >
                  {t(`discover.map.datePreset.${preset}`)}
                </button>
              ))}
              <button
                type="button"
                className={`map-filters-preset ${draft.datePreset === 'custom' ? 'active' : ''}`}
                onClick={toggleCustomDateRange}
              >
                {t('discover.map.datePreset.custom', 'Własne')}
              </button>
            </div>

            {draft.datePreset === 'custom' && (
              <>
                <label className="map-filters-label" htmlFor="map-filter-date-from">
                  {t('discover.map.dateFrom', 'Data od')}
                </label>
                <input
                  id="map-filter-date-from"
                  type="date"
                  className="map-filters-input"
                  value={draft.dateFrom}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      dateFrom: e.target.value,
                      datePreset: 'custom',
                    }))
                  }
                />

                <label className="map-filters-label" htmlFor="map-filter-date-to">
                  {t('discover.map.dateTo', 'Data do')}
                </label>
                <input
                  id="map-filter-date-to"
                  type="date"
                  className="map-filters-input"
                  value={draft.dateTo}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      dateTo: e.target.value,
                      datePreset: 'custom',
                    }))
                  }
                />
              </>
            )}

            <span className="map-filters-label">{t('discover.map.pricePerPersonPln', 'Cena (zł / osoba)')}</span>
            <div className="map-filters-row-2">
              <input
                type="number"
                min="0"
                step="0.01"
                className="map-filters-input map-filters-input--half"
                placeholder={t('discover.map.priceMin', 'Od')}
                value={draft.priceMin}
                onChange={(e) => setField('priceMin', e.target.value)}
                aria-label={t('discover.map.priceMin', 'Od')}
              />
              <input
                type="number"
                min="0"
                step="0.01"
                className="map-filters-input map-filters-input--half"
                placeholder={t('discover.map.priceMax', 'Do')}
                value={draft.priceMax}
                onChange={(e) => setField('priceMax', e.target.value)}
                aria-label={t('discover.map.priceMax', 'Do')}
              />
            </div>

            <span className="map-filters-label">{t('discover.map.freeOrPaid', 'Bezpłatne / płatne')}</span>
            <div className="map-filters-segment" role="group" aria-label={t('discover.map.freeOrPaid', 'Bezpłatne / płatne')}>
              <button
                type="button"
                className={`map-filters-segment-btn ${draft.isFree === null ? 'active' : ''}`}
                onClick={() => setField('isFree', null)}
              >
                {t('discover.map.allPrices', 'Wszystkie')}
              </button>
              <button
                type="button"
                className={`map-filters-segment-btn ${draft.isFree === true ? 'active' : ''}`}
                onClick={() => setField('isFree', true)}
              >
                {t('discover.map.freeOnly', 'Bezpłatne')}
              </button>
              <button
                type="button"
                className={`map-filters-segment-btn ${draft.isFree === false ? 'active' : ''}`}
                onClick={() => setField('isFree', false)}
              >
                {t('discover.map.paidOnly', 'Płatne')}
              </button>
            </div>

            <span className="map-filters-label">{t('discover.map.participantsCount', 'Liczba uczestników (max)')}</span>
            <div className="map-filters-row-2">
              <input
                type="number"
                min="1"
                className="map-filters-input map-filters-input--half"
                placeholder={t('discover.map.minParticipants', 'Min')}
                value={draft.minParticipants}
                onChange={(e) => setField('minParticipants', e.target.value)}
                aria-label={t('discover.map.minParticipants', 'Minimum')}
              />
              <input
                type="number"
                min="1"
                className="map-filters-input map-filters-input--half"
                placeholder={t('discover.map.maxParticipants', 'Max')}
                value={draft.maxParticipants}
                onChange={(e) => setField('maxParticipants', e.target.value)}
                aria-label={t('discover.map.maxParticipants', 'Maksimum')}
              />
            </div>

            <label
              className={`map-filters-check ${friendsFilterDisabled ? 'map-filters-check--disabled' : ''}`}
              title={
                friendsFilterDisabled
                  ? t('discover.map.friendsFilterDisabledHint', 'Zaloguj się i dodaj znajomych, aby użyć tego filtra.')
                  : undefined
              }
            >
              <input
                type="checkbox"
                checked={draft.friendsOnly}
                disabled={friendsFilterDisabled}
                onChange={(e) => setField('friendsOnly', e.target.checked)}
              />
              <span>{t('discover.map.friendsEvents', 'Wydarzenia znajomych (organizator)')}</span>
            </label>

            <label
              className={`map-filters-check ${mineFilterDisabled ? 'map-filters-check--disabled' : ''}`}
              title={
                mineFilterDisabled
                  ? t('discover.map.mineFilterDisabledHint', 'Zaloguj się, aby filtrować własne wydarzenia.')
                  : undefined
              }
            >
              <input
                type="checkbox"
                checked={draft.mineOnly}
                disabled={mineFilterDisabled}
                onChange={(e) => setField('mineOnly', e.target.checked)}
              />
              <span>{t('discover.map.myEvents', 'Moje wydarzenia')}</span>
            </label>
          </div>

          <div className="map-filters-actions">
            <button type="button" className="map-filters-button map-filters-button--secondary" onClick={reset}>
              {t('discover.map.clearFilters', 'Wyczyść')}
            </button>
            <button type="button" className="map-filters-button map-filters-button--primary" onClick={apply}>
              {t('discover.map.applyFilters', 'Zastosuj')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default MapFiltersPanel;
